
import Vue from "vue";
import organizationApi from "@/services/api.organizationSettings";
import { Getter, namespace } from "vuex-class";
import { UserPermissions } from "@/store/types";
import Component from "vue-class-component";
import SubjectLineTokenField from "@/components/notification-templates/SubjectLineTokenField.vue";
import RichTextEditorPlaceholder from "@/types/RichTextEditorPlaceholder";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldTypes } from "@/store/custom-fields/types";

const CustomFields = namespace("customFields");

@Component({
	components: {
		"subject-line-token-field": SubjectLineTokenField,
	},
})
export default class NotificationSubjectLineForm extends Vue {
	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@CustomFields.State private notificationTemplateCustomFields: CustomFieldDto[];
	@CustomFields.State private areaCustomFields: CustomFieldDto[];
	@CustomFields.Action private retrieveCustomFields: ({ tableType, live, skipCancel, excludeTypeIds }) => Promise<void>;

	private isUpdating: boolean = false;
	private subjectNotificationLine: string = "";
	private newSubjectNotificationLine: string = "";

	private customFields: RichTextEditorPlaceholder[] = [];
	private placeholderFields: RichTextEditorPlaceholder[] = [
		{
			id: "areaName",
			label: "Affected Area",
		},
		{
			id: "areaAddress",
			label: "Area Address",
		},
		{
			id: "areaReferenceId",
			label: "Area Unique Identifier",
		},
		{
			id: "eventCreatedDate",
			label: "Event Started",
		},
		{
			id: "eventOutcome",
			label: "Event Outcome",
		},
		{
			id: "eventOutcomeNote",
			label: "Event Outcome Note",
		},
		{
			id: "note",
			label: "Note",
		},
		{
			id: "senderName",
			label: "Sender Name",
		},
		{
			id: "eventId",
			label: "Event ID",
		},
	];

	private async beforeMount(): Promise<void> {
		try {
			this.subjectNotificationLine = (await organizationApi.retrieveNotificationSubjectLine(null)).subjectLine;
		} catch {
			this.subjectNotificationLine = "";
		}

		if (this.hasRequiredCustomFieldAccess) {
			await this.retrieveCustomFields({
				tableType: CustomFieldTableTypes.NotificationTemplate,
				live: true,
				skipCancel: false,
				excludeTypeIds: [],
			});
		}

		// Area custom fields land in a separate store slice, so this does not clobber the
		// Notification Template fields fetched above. Icon fields are excluded as they were
		// before this feature - an icon has no meaningful text form in a subject line.
		if (this.showAreaCustomFieldPicker) {
			await this.retrieveCustomFields({
				tableType: CustomFieldTableTypes.Area,
				live: true,
				skipCancel: false,
				excludeTypeIds: [CustomFieldTypes.Icon],
			});
		}

		this.newSubjectNotificationLine = this.subjectNotificationLine;
	}

	private haveSettingsChanged(): boolean {
		return this.isUpdating || this.newSubjectNotificationLine === this.subjectNotificationLine;
	}

	private async confirmUpdateNotificationSubjectLineSettings(): Promise<void> {
		this.isUpdating = true;
		try {
			const response = await organizationApi.createOrUpdateNotificationSubjectLine({
				subjectLine: this.newSubjectNotificationLine,
			});
			this.subjectNotificationLine = response.subjectLine;
			this.newSubjectNotificationLine = response.subjectLine;

			this.$notify({
				type: "success",
				title: "Success",
				text: "Notification Subject Line updated",
			});
			this.isUpdating = false;
		} catch {
			this.isUpdating = false;
		}
	}

	private resetChanges(): void {
		this.newSubjectNotificationLine = this.subjectNotificationLine;
	}

	// Deliberately NOT gated on the "Suite" feature. Notification Template custom fields
	// exist only in Response, not in Suite, so a tenant whose core configuration is
	// managed by Suite is no less able to use them. The Suite check here previously
	// guarded the Area custom-field picker, which had different semantics; carrying it
	// over to these fields hid them from every non-Suite tenant.
	// Matches NotificationTemplateSetup.vue, which gates the equivalent per-template
	// picker on the feature and permission alone.
	private get hasRequiredCustomFieldAccess(): boolean {
		return (
			!!this.getFeature(["CustomFields", "NotificationTemplates"]) &&
			this.permissions.canViewCustomFields
		);
	}

	// Area custom fields were offered on this form before the subject-placeholder work and are kept,
	// so a tenant relying on them does not silently lose the ability to insert them. Their gate is
	// the original one, Suite included - that restriction was made deliberately for Area fields
	// (2025-05-16) and only became wrong when it was applied to Notification Template fields, which
	// exist in Response only. It is deliberately not tied to the newer custom-field flag, because
	// this capability predates it.
	private get showAreaCustomFieldPicker(): boolean {
		return (
			!!this.getFeature(["Suite"]) &&
			!!this.getFeature(["CustomFields", "NotificationTemplates"]) &&
			this.permissions.canViewCustomFields
		);
	}

	private get getAreaCustomFieldPlaceholders(): RichTextEditorPlaceholder[] {
		if (!this.showAreaCustomFieldPicker || !Array.isArray(this.areaCustomFields)) {
			return [];
		}

		return this.areaCustomFields.map((cf) => ({ id: cf.id, label: cf.title }));
	}

	// Custom fields on the tenant subject line are gated behind a dedicated feature flag.
	private get showCustomFieldPicker(): boolean {
		return (
			this.hasRequiredCustomFieldAccess &&
			!!this.getFeature(["TenantSettings", "NotificationSubjectLine", "CustomFields"])
		);
	}

	// Manual fields on the tenant subject line are gated behind their own feature flag,
	// independent of the custom-field flag.
	private get showManualFieldPicker(): boolean {
		return !!this.getFeature(["TenantSettings", "NotificationSubjectLine", "ManualFields"]);
	}

	private get getEligibleSubjectCustomFieldPlaceholders(): RichTextEditorPlaceholder[] {
		this.customFields = [];
		if (!this.hasRequiredCustomFieldAccess) {
			return this.customFields;
		}

		if (Array.isArray(this.notificationTemplateCustomFields)) {
			this.notificationTemplateCustomFields
				.filter((cf) => cf.options?.eligibleForNotificationSubject === true)
				.forEach((cf) => {
					this.customFields.push({ id: cf.id, label: cf.title });
				});
		}
		return this.customFields;
	}
}
